import { forkJoin, from, lastValueFrom, Observable, of, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  retry,
  takeUntil
} from 'rxjs/operators';
import { GLOBAL } from '../shared/constants/global';
import { Util } from '../shared/util';
import { PhdsoftErrorService } from '../shared/services/phdsoft-error.service';
import { PhdSoftPathValuePipe } from '@phdsoft/pipes';
import { RequestCacheWithMap } from '../shared/services/phdsoft-cache.service';
import { _RecycleViewRepeaterStrategy } from '@angular/cdk/collections';
import { NotificationService } from 'src/app/signalr/notification.service';
import { OfflineChange, OfflineChangesService, DeduplicationRule } from '../shared/services/offline-changes.service';
import { OfflinePhotoService } from '@app/shared/services/media-services/offline-photo.service';

export type genericResponse<TModel> = TModel | TModel[] | [] | {} | null;

@Injectable({
  providedIn: 'root',
})
export class PhdsoftCommonService {
  private taskSubject = new Subject<Observable<any>>(); // Task stream
  private destroy$ = new Subject<void>(); // For stopping all tasks

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': GLOBAL.APPLICATION_TYPE_JSON,
      Accept: 'application/json; text/plain',
    }),
    params: new HttpParams(),
  };

  constructor(
    private readonly http: HttpClient,
    private readonly httpErrorHandler: PhdsoftErrorService,
    private readonly requestCacheWithMap: RequestCacheWithMap,
    private readonly offlineChanges: OfflineChangesService,
    private readonly notificationService: NotificationService,
    private readonly offlinePhotoService: OfflinePhotoService
  ) { }

  private async verifyConnection(): Promise<boolean> {
    if (!window.navigator.onLine) {
      console.log('Offline Sync: Skipped - device is offline');
      return false;
    }

    // Robust connection verification - ensure stable connection for 2 seconds
    console.log('Offline Sync: Verifying stable internet connection...');
    const connectionCheckDuration = 2000; // 2 seconds
    const checkInterval = 500; // Check every 500ms
    const requiredChecks = connectionCheckDuration / checkInterval; // 4 checks
    let successfulChecks = 0;

    for (let i = 0; i < requiredChecks; i++) {
      if (!window.navigator.onLine) {
        console.log('Offline Sync: Aborted - connection lost during verification');
        return false;
      }

      // Test actual connectivity by making a lightweight request
      try {
        const response = await fetch('/statics/icons/favicon-32x32.png', {
          method: 'HEAD',
          cache: 'no-cache',
          signal: AbortSignal.timeout(1000) // 1 second timeout
        });

        if (response.ok) {
          successfulChecks++;
        }
      } catch (error) {
        console.log('Offline Sync: Connection check failed, retrying...');
      }

      // Wait before next check (except for the last iteration)
      if (i < requiredChecks - 1) {
        await new Promise(resolve => setTimeout(resolve, checkInterval));
      }
    }

    // Only proceed if we had stable connection for the full duration
    if (successfulChecks < requiredChecks) {
      console.log('Offline Sync: Aborted - unstable internet connection');
      return false;
    }

    return true;
  }

  async offlineSync() {
    // Check if there are any offline changes to sync
    console.log('Offline Sync: Checking for offline changes...');
    const changeCount = await this.offlineChanges.getCount();

    if (changeCount === 0) {
      console.log('Offline Sync: No offline changes found - skipping sync');
      return;
    }
    console.log(`Offline Sync: Found ${changeCount} offline changes - starting sync...`);

    if (!await this.verifyConnection()) {
      return;
    }

    const title: string = "Offline Synchronization";

    try {
        this.notificationService.addInProgressNotification(title);

        // Step 1: Define deduplication rules
        const deduplicationRules = [
          { elementType: 'inspectionElement', baseUrl: '/Inspection/SaveInspectionElements' },
          { elementType: 'inspectionElement', baseUrl: '/InspectionPlan/ManagePlan' }
        ];

        console.log('Starting offline sync with hashed deduplication...');

        // Step 2: Deduplicate entries directly from database (removes duplicates)
        console.log('Deduplicating entries using hashed keys...');
        await this.offlineChanges.deduplicateWithHashedKeys(deduplicationRules);
        console.log('Deduplication completed');

        let successCount = 0;
        let failedChanges = [];

        // Step 3: Process each remaining change one by one (stream from database)
        console.log('Processing changes one by one...');
        const allKeys = await this.offlineChanges.getAllKeys();

        for (const key of allKeys) {
            const change = await this.offlineChanges.getItem(key);

            if (!change) {
                console.warn(`Offline change with key ${key} not found, skipping...`);
                continue;
            }

            try {
                const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
                let request: HttpRequest<any>;

                if (change.element === 'imageAnomaly') {
                    const formData = new FormData();
                    formData.append("pFormFiles", change.file);
                    request = new HttpRequest<any>(change.method, change.url, formData);
                } else if (change.element === 'media') {
                    const formData = new FormData();
                    formData.append("commonMediaViewModel", change.parameters);
                    formData.append("formFiles", change.file);
                    request = new HttpRequest<any>(change.method, change.url, formData);
                } else {
                    request = new HttpRequest<any>(change.method, change.url, change.parameters, { headers });
                }

                const response = await lastValueFrom(this.http.request(request));

                if (response && 'body' in response && response.status === 200) {
                    await this.offlineChanges.removeItem(change.id);
                    successCount++;
                } else if (response && 'body' in response && response.status === 204) {
                    await this.offlineChanges.removeItem(change.id);
                    successCount++;
                } else {
                    failedChanges.push(change);
                }
            } catch (error) {
                failedChanges.push(change);
                console.error(error);
            }

            // Allow other operations to run between requests
            await new Promise(resolve => setTimeout(resolve, 0));
        }

        console.log(`Offline sync completed: ${successCount} successful, ${failedChanges.length} failed`);

        await this.offlinePhotoService.uploadPendingPhotos();

        if (failedChanges.length > 0) {
            this.notificationService.addFailedNotification(title);
            console.error(`Sync completed with ${failedChanges.length} failed items. ${successCount} items synced successfully.`);
        } else {
            this.notificationService.addSuccessNotification(title);
        }
    } catch (error) {
        this.notificationService.addFailedNotification(title);
        console.error(error);
        throw error;
    }
  }

  public async getFile(url: string, offline?: boolean): Promise<genericResponse<any>> {
    const httpOptionsFile = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        //'Cache-Control': 'private',
        'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
        'Pragma': 'no-cache',
        'Expires': '0',
        Accept: 'application/json; text/plain',
      }),
      params: new HttpParams(),
    };

    let _RETURN: genericResponse<any>;
    const _REQUEST = new HttpRequest('GET', url, null, httpOptionsFile);

    const offlineResponse = undefined;//await this.requestCacheWithMap.getFromUrl(url, true);

    if (!Util.isNullOrUndefined(offlineResponse)) {
      _RETURN = offlineResponse;
    } else {
      await this.http
        .request(_REQUEST)
        .pipe(debounceTime(500), distinctUntilChanged())
        .toPromise()
        .then(async (response: HttpResponse<any>) => {
          if (response.ok) {
            _RETURN = response.body;
            //await this.requestCacheWithMap.putArrayBuffer(_REQUEST, response, offline);
          } else {
            this.httpErrorHandler.snack('Error', 'Error on download files');
            // this.notificationService.addFailedNotification('Error on Download Files')
            this.httpErrorHandler.handleError({
              Id: response.body.Code,
              status: response.body.Code,
              Description: response.body.Data as string,
              Exception: { error: response.body.Data },
            });
          }
        })
        .catch((error) => {
          this.httpErrorHandler.handleError(error);
        });
    }
    return _RETURN;
  }

  public async getFileArrayBuffer(url: string, offline?: boolean, saveOffline?:boolean): Promise<genericResponse<any>> {
    let _RETURN: genericResponse<any>;
    const _REQUEST = new HttpRequest('GET', url, null, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=UTF-8',
        'Cache-Control': 'max-age=0',
        'Accept-Language': 'en-US,en;q=0.9,pt;q=0.8',
        Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.9',
      }),
      params: new HttpParams(),
      responseType: 'arraybuffer'
    });
    const urlWithoutParams = url.substring(0, url.indexOf('?'));
    let offlineResponse
    if (offline) {
      offlineResponse = await this.requestCacheWithMap.getFromUrl(urlWithoutParams, true);
    }

    if (!Util.isNullOrUndefined(offlineResponse?.body)) {
      _RETURN = offlineResponse.body;
    } else {
      await this.http
        .request(_REQUEST)
        .pipe(debounceTime(500), distinctUntilChanged(), retry(3))
        .toPromise()
        .then(async (response: HttpResponse<any>) => {
          if (response.ok) {
            _RETURN = response.body;
            if (saveOffline){
              await this.requestCacheWithMap.putFromUrl(urlWithoutParams, response, true, true);
            }
          } else {
            this.httpErrorHandler.snack('Error', 'Error on download files');
            // this.notificationService.addFailedNotification('Error on Download Files');
            this.httpErrorHandler.handleError({
              Id: response.body.Code,
              status: response.body.Code,
              Description: response.body.Data as string,
              Exception: { error: response.body.Data },
            });
          }
        })
        .catch((error) => {
          this.httpErrorHandler.handleError(error);
        });
    }
    return _RETURN;
  }

  public mobileOrDesktop(): boolean {
    if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i.test(
        navigator.userAgent,
      )
    ) {
      return true;
    }
    return false;
  }

  public async checkOnlineStatus(microserviceUrl: string): Promise<boolean> {
    // Preliminary check using navigator.onLine
    if (!navigator.onLine) {
      return false;
    }

    // Check server connectivity
    if (!this.mobileOrDesktop()) {
      try {
        const request = new HttpRequest(
          'GET',
          `${microserviceUrl}Authenticator/online`,
          null,
          this.httpOptions
        );

        const response = await lastValueFrom(
          this.http.request(request).pipe(
            catchError(() => of(false)) // Return false on error
          )
        );

        // If the response is a valid HttpResponse, return its status
        if (response instanceof HttpResponse) {
          return response.ok;
        }
        return false; // Default to false if response is not HttpResponse
      } catch (error) {
        return false; // Return false on any other unexpected error
      }
    }

    return true; // Default to true for mobile devices (if logic still applies)
  }

  public runBackground<T>(...items: (Promise<T> | Observable<T>)[]): void {
    const observables = items.map((item) =>
      item instanceof Promise ? from(item) : item
    );
    observables.forEach((obs) => {
      this.taskSubject.next(obs.pipe(takeUntil(this.destroy$))); // Add task with cancellation
    });
  }

  public async awaitBackground(): Promise<any[]> {
    const tasks: Observable<any>[] = [];
    this.taskSubject.subscribe((task) => tasks.push(task)); // Collect active tasks

    const results = await forkJoin(tasks).toPromise(); // Wait for all tasks to complete
    return results || [];
  }

  public clearPromiseList(): void {
    this.stopPromises(); // Stop current tasks
    this.destroy$ = new Subject<void>(); // Reset destroy subject
    this.taskSubject = new Subject<Observable<any>>(); // Reset task stream
  }

  public stopPromises(): void {
    this.destroy$.next(); // Signal all tasks to stop
    this.destroy$.complete(); // Complete destroy stream
    this.taskSubject.complete(); // Complete task subject
  }

  createStringFilter(pList: any[], cols: any): Promise<any[]> {
    try {
      return new Promise((resolve, reject) => {
        pList.forEach((x, i) => {
          const s = cols
            .map((y) => {
              if (y.pipe) {
                return y.pipe.transform(new PhdSoftPathValuePipe().transform(x, y as any) || '');
              }
              return new PhdSoftPathValuePipe().transform(x, y as any) || '';
            })
            .join('')
            .toLocaleLowerCase()
            .replace(' ', '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
          pList[i].StringFilter = s;
        });
        resolve(pList);
      });
    } catch (error) {
      console.error('[PhdsoftCommonService] => createStringFilter', error);
    }
  }

  filter(value, pList: any[]): any[] {
    try {
      const val = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
      const temp = pList.filter((x) => x.StringFilter.indexOf(val.toLowerCase()) !== -1 || !val);
      pList = temp;
      return pList;
    } catch (error) {
      console.error('[PhdsoftCommonService] => filter', error);
    }
  }
}
